import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import {
  toggleRightDock,
  setActiveTab,
  toggleEditorDocked,
  toggleGalleryDocked,
  setDockWidth
} from '../../../Redux/actions/rightDock.js';
import { openModal, closeModal } from '../../../Redux/actions/ui.js';
import { selectPermissionCapabilities } from '../../../utils/permissions.js';
import { getDockControlIcon, getDockControlIconClass } from '../dockControls.js';

export function isAlwaysAvailableEditorDockModal(activeModal) {
  return activeModal === 'SettingsEditor' || activeModal === 'HelpInfo';
}

const EDITOR_DOCK_MODAL_NAMES = new Set([
  'HelpInfo',
  'SettingsEditor',
  'InventumLabelEditor',
  'InventumLightsEditor',
  'InventumClippingEditor',
  'InventumWorldEditor',
  'InventumSlideEditor',
  'InventumSceneFileEditor'
]);

export function isEditorDockModal(activeModal) {
  return EDITOR_DOCK_MODAL_NAMES.has(activeModal);
}

export function shouldOpenDefaultEditorDockModal({ activeModal } = {}) {
  return !isEditorDockModal(activeModal);
}

export function canShowEditorDockPanel({ canAccessEditorUI, editMode, activePageType, activeModal, editorDocked }) {
  return !!(canAccessEditorUI && editMode) ||
    activePageType === 'INVENTUM_MASTER' ||
    !!editorDocked ||
    isAlwaysAvailableEditorDockModal(activeModal);
}

export function getDefaultEditorDockModal({ canAccessEditorUI, editMode }) {
  return canAccessEditorUI && editMode ? 'InventumLabelEditor' : 'SettingsEditor';
}

export function getDockStateAfterEditorUndock({ galleryDocked }) {
  if (galleryDocked) {
    return { visible: true, activeTab: 'gallery' };
  }

  return { visible: false, activeTab: null };
}

export function getDockStateAfterGalleryUndock({ editorDocked }) {
  if (editorDocked) {
    return { visible: true, activeTab: 'editor' };
  }

  return { visible: false, activeTab: null };
}

// Pull-out tab component (mirrors left sidebar)
function RightDockTab({ visible, toggleDock, hasActivePanels, dockWidth, isResizing, onOpen, onClose, uiHideMode }) {
  const handleClick = () => {
    if (!visible) {
      // Opening the dock - call onOpen callback
      onOpen();
    } else {
      // Closing the dock - call onClose to properly reset states
      onClose();
    }
  };

  if (uiHideMode === 'ALL' || uiHideMode === 'ANIMATION') {
    return null;
  }
  
  return (
    <div
      className={`rightDockToggleTab${visible ? ' moved' : ''}${isResizing ? ' resizing' : ''}`}
      onClick={handleClick}
      style={visible ? { '--dock-width': `${dockWidth}px` } : {}}
    >
      <i className="material-icons notranslate">
        {visible ? 'chevron_right' : 'chevron_left'}
      </i>
      {hasActivePanels && !visible ? <div className="rightDockTabBadge"></div> : null}
    </div>
  );
}

// Tab button component
function TabButton({ id, label, isActive, onClick, hasDockButton, onDockToggle, isDocked }) {
  return (
    <div className={`DockTab${isActive ? ' active' : ''}`} onClick={onClick}>
      <span>{label}</span>
      {hasDockButton && isActive && (
        <div
          className="DockActionButton"
          onClick={(e) => {
            e.stopPropagation();
            onDockToggle();
          }}
          title={isDocked ? 'Undock (Float)' : 'Dock'}
        >
          <i className={getDockControlIconClass({ isDocked })}>
            {getDockControlIcon({ isDocked })}
          </i>
        </div>
      )}
    </div>
  );
}

// Main dock container component
function RightDockContainer(props) {
  const [isResizing, setIsResizing] = useState(false);
  const [hasAnimationSelected, setHasAnimationSelected] = useState(false);
  const resizeStartRef = useRef({ x: 0, width: 0 });
  const dockPanelRef = useRef(null);
  const dockContentRef = useRef(null);
  // Tabs and editor modals render their own scroll containers. Keep only numeric
  // offsets and structural paths so a remounted panel can be restored safely.
  const dockScrollPositionsRef = useRef(new Map());

  const saveDockScrollPosition = useCallback((event) => {
    const contentArea = dockContentRef.current;
    const target = event.target;

    if (!contentArea || !(target instanceof HTMLElement) || !contentArea.contains(target)) {
      return;
    }

    const path = [];
    let element = target;

    while (element !== contentArea) {
      const parent = element.parentElement;
      if (!parent) {
        return;
      }

      const index = Array.prototype.indexOf.call(parent.children, element);
      if (index < 0) {
        return;
      }

      path.unshift(index);
      element = parent;
    }

    const panelId = props.activeTab === 'editor' ? props.activeModal || 'default' : props.activeTab;
    dockScrollPositionsRef.current.set(`${props.activeTab}:${panelId}:${path.join('.')}`, {
      path,
      scrollTop: target.scrollTop
    });
  }, [props.activeModal, props.activeTab]);

  useEffect(() => {
    const panel = dockPanelRef.current;
    if (!panel) {
      return undefined;
    }

    panel.addEventListener('scroll', saveDockScrollPosition, true);
    return () => panel.removeEventListener('scroll', saveDockScrollPosition, true);
  }, [saveDockScrollPosition]);

  useLayoutEffect(() => {
    if (!props.dockVisible || !dockContentRef.current) {
      return undefined;
    }

    const restoreDockScrollPositions = () => {
      const contentArea = dockContentRef.current;
      if (!contentArea) {
        return;
      }

      const panelId = props.activeTab === 'editor' ? props.activeModal || 'default' : props.activeTab;
      const scrollPositionPrefix = `${props.activeTab}:${panelId}:`;

      dockScrollPositionsRef.current.forEach(({ path, scrollTop }, key) => {
        if (!key.startsWith(scrollPositionPrefix)) {
          return;
        }

        const target = path.reduce((element, childIndex) => element && element.children[childIndex], contentArea);
        if (!(target instanceof HTMLElement)) {
          return;
        }

        const maxScrollTop = Math.max(0, target.scrollHeight - target.clientHeight);
        target.scrollTop = Math.min(scrollTop, maxScrollTop);
      });
    };

    restoreDockScrollPositions();
    const animationFrame = requestAnimationFrame(restoreDockScrollPositions);
    return () => cancelAnimationFrame(animationFrame);
  }, [props.dockVisible, props.activeTab, props.editorDocked, props.galleryDocked, props.activeModal, props.uiHideMode]);

  // Check if an animation is selected
  useEffect(() => {
    const checkAnimationState = () => {
      const animState = Inventum.animations.getState();
      setHasAnimationSelected(animState.name !== 'No Animation Selected');
    };
    
    // Initial check
    checkAnimationState();
    
    // Register callback to update when animation changes
    Inventum.animations.registerStateCallback(checkAnimationState);
    
    // Note: We don't unregister because there's no public API for that
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      width: props.dockWidth
    };
  }, [props.dockWidth]);

  // Handle resize move
  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    
    const deltaX = resizeStartRef.current.x - e.clientX;
    const newWidth = resizeStartRef.current.width + deltaX;
    
    props.setDockWidth(newWidth);
  }, [isResizing, props]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove resize event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'ew-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Determine if any panels are available
  const hasEditorDockPanel = canShowEditorDockPanel(props);
  const defaultEditorModal = getDefaultEditorDockModal(props);
  const hasGalleryAccess = true; // Gallery is always available
  const hasActivePanels = hasEditorDockPanel || hasGalleryAccess;

  // Handle editor dock toggle
  const handleEditorDockToggle = useCallback((fromTab = false) => {
    if (props.editorDocked) {
      // Undock the editor. If gallery remains docked, keep the right dock open
      // and hand focus to Gallery; otherwise close the right dock.
      const currentModal = props.activeModal;
      const nextDockState = getDockStateAfterEditorUndock(props);

      props.toggleEditorDocked(false);
      if (nextDockState.activeTab) {
        props.setActiveTab(nextDockState.activeTab);
      }
      props.toggleRightDock(nextDockState.visible);

      // Reopen the modal as floating if there was an active one
      if (currentModal && currentModal.startsWith('Inventum')) {
        setTimeout(() => props.openModal(currentModal), 100);
      }
    } else {
      // When opening from tab, set docked FIRST, then open dock, then modal
      // This ensures the modal renders in the dock, not as floating
      if (fromTab) {
        // Step 1: Set docked state first
        props.toggleEditorDocked(true);
        props.setActiveTab('editor');
        
        // Step 2: Open dock immediately
        props.toggleRightDock(true);
        
        // Step 3: Open modal after a tick to ensure state has updated
        if (!props.activeModal || !props.activeModal.startsWith('Inventum')) {
          // Use requestAnimationFrame to wait for React to flush state updates
          requestAnimationFrame(() => {
            props.openModal(defaultEditorModal);
          });
        }
      } else {
        // Normal docking from floating modal
        if (props.activeModal && props.activeModal.startsWith('Inventum')) {
          props.closeModal();
        }
        props.toggleEditorDocked(true);
        props.toggleRightDock(true);
        props.setActiveTab('editor');
        
        if (!props.activeModal || !props.activeModal.startsWith('Inventum')) {
          setTimeout(() => props.openModal(defaultEditorModal), 50);
        }
      }
    }
  }, [props]);

  // Handle gallery dock toggle
  const handleGalleryDockToggle = useCallback(() => {
    if (props.galleryDocked) {
      // Undock the gallery. If editor remains docked, keep the right dock open
      // and hand focus to Editor; otherwise preserve the existing close behavior.
      const nextDockState = getDockStateAfterGalleryUndock(props);

      props.toggleGalleryDocked(false);
      if (nextDockState.activeTab) {
        props.setActiveTab(nextDockState.activeTab);
      }
      
      // Use setTimeout to ensure state updates are processed before showing floating gallery
      setTimeout(() => {
        if (props.toggleGallery) {
          props.toggleGallery(true);
        }
        props.toggleRightDock(nextDockState.visible);
      }, 10);
    } else {
      // Dock: close floating gallery, dock gallery, open dock
      if (props.toggleGallery) {
        props.toggleGallery(false);
      }
      props.toggleGalleryDocked(true);
      props.toggleRightDock(true);
      props.setActiveTab('gallery');
    }
  }, [props]);

  // Handle opening the dock from the tab
  const handleDockOpen = useCallback(() => {
    // If there's already a floating editor modal open, don't dock it - show gallery instead
    const hasFloatingEditor = props.activeModal && props.activeModal.startsWith('Inventum') && !props.editorDocked;
    
    // Check if editor is already docked (just needs to reopen the dock)
    if (props.editorDocked) {
      // Editor is already docked, just open the dock and show editor tab
      props.toggleRightDock(true);
      props.setActiveTab('editor');
      // Reopen the modal if it's not already active
      if (!props.activeModal) {
        setTimeout(() => props.openModal(defaultEditorModal), 50);
      }
    } else if (props.galleryDocked) {
      // Gallery is already docked, just open the dock and show gallery tab
      props.toggleRightDock(true);
      props.setActiveTab('gallery');
    } else if (hasEditorDockPanel && !hasFloatingEditor) {
      // No panels docked yet, dock editor by default when the editor panel is available
      handleEditorDockToggle(true);
    } else if (hasGalleryAccess) {
      // Show gallery if no editor access or if editor is already floating
      props.toggleGalleryDocked(true);
      props.toggleRightDock(true);
      props.setActiveTab('gallery');
    }
  }, [hasEditorDockPanel, hasGalleryAccess, handleEditorDockToggle, handleGalleryDockToggle, props]);

  // Handle closing the dock from the tab
  const handleDockClose = useCallback(() => {
    // Close the modal FIRST if editor is docked
    const wasEditorDocked = props.editorDocked;
    
    // Close the active modal only if it's an editor modal shown inside the dock.
    // This includes HelpInfo, which does not use the Inventum* prefix.
    if (props.activeModal && wasEditorDocked &&
        (props.activeModal.startsWith('Inventum') ||
          props.activeModal === 'SettingsEditor' ||
          props.activeModal === 'HelpInfo')) {
      props.closeModal();
    }
    
    // Then reset docked states
    if (props.editorDocked) {
      props.toggleEditorDocked(false);
    }
    if (props.galleryDocked) {
      props.toggleGalleryDocked(false);
    }
    
    // Finally close the dock
    props.toggleRightDock(false);
  }, [props]);

  // Handle tab change - ensure docked state is set when switching tabs
  const handleTabChange = useCallback((tabId) => {
    props.setActiveTab(tabId);
    
    // Ensure the corresponding docked state is true when switching to a tab in an open dock
    if (props.dockVisible) {
      if (tabId === 'gallery' && !props.galleryDocked) {
        props.toggleGalleryDocked(true);
      } else if (tabId === 'editor') {
        if (!props.editorDocked) {
          props.toggleEditorDocked(true);
        }

        // Opening Gallery can leave the editor docked but without an active
        // editor modal, especially after review-mode transitions collapse the
        // right dock. Ensure the Editor tab has content whenever it is selected.
        if (shouldOpenDefaultEditorDockModal(props)) {
          setTimeout(() => props.openModal(defaultEditorModal), 50);
        }
      }
    }
  }, [props]);

  // Render content based on active tab
  const renderContent = () => {
    if (props.activeTab === 'editor' && props.editorDocked) {
      return props.children?.editor;
    } else if (props.activeTab === 'gallery' && props.galleryDocked) {
      return props.children?.gallery;
    }
    return null;
  };

  if (!hasActivePanels) {
    return null;
  }

  // Alt+H toggles uiHideMode='ALL' for recording mode; hide the entire right dock UI,
  // including the teal pull-out arrow tab.
  if (props.uiHideMode === 'ALL' || props.uiHideMode === 'ANIMATION') {
    return null;
  }

  return (
    <div className="RightDockContainer">
      <RightDockTab
        visible={props.dockVisible}
        toggleDock={props.toggleRightDock}
        hasActivePanels={hasActivePanels}
        dockWidth={props.dockWidth}
        isResizing={isResizing}
        onOpen={handleDockOpen}
        onClose={handleDockClose}
        uiHideMode={props.uiHideMode}
      />
      
      <div
        ref={dockPanelRef}
        className={`DockPanel${props.dockVisible ? ' visible' : ''}`}
        style={{ '--dock-width': `${props.dockWidth}px` }}
      >
        <div
          className={`DockResizeHandle${isResizing ? ' resizing' : ''}`}
          onMouseDown={handleResizeStart}
        />
        
        <div className="DockTabBar">
          {hasEditorDockPanel && (
            <TabButton
              id="editor"
              label="Editor"
              isActive={props.activeTab === 'editor'}
              onClick={() => handleTabChange('editor')}
              hasDockButton={true}
              onDockToggle={handleEditorDockToggle}
              isDocked={props.editorDocked}
            />
          )}
          
          {hasGalleryAccess && (
            <TabButton
              id="gallery"
              label="Gallery"
              isActive={props.activeTab === 'gallery'}
              onClick={() => handleTabChange('gallery')}
              hasDockButton={hasAnimationSelected}
              onDockToggle={handleGalleryDockToggle}
              isDocked={props.galleryDocked}
            />
          )}
        </div>
        
        <div ref={dockContentRef} className="DockContentArea">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  const caps = selectPermissionCapabilities(state);

  return {
    dockVisible: state.rightDock.visible,
    activeTab: state.rightDock.activeTab,
    editorDocked: state.rightDock.editorDocked,
    galleryDocked: state.rightDock.galleryDocked,
    dockWidth: state.rightDock.width,
    canAccessEditorUI: caps.canAccessEditorUI,
    editMode: state.ui.editMode,
    activePageType: state.activePage && state.activePage.type,
    activeModal: state.ui.activeModal,
    uiHideMode: state.ui.hideMode
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggleRightDock: (visible) => dispatch(toggleRightDock(visible)),
  setActiveTab: (tab) => dispatch(setActiveTab(tab)),
  toggleEditorDocked: (docked) => dispatch(toggleEditorDocked(docked)),
  toggleGalleryDocked: (docked) => dispatch(toggleGalleryDocked(docked)),
  setDockWidth: (width) => dispatch(setDockWidth(width)),
  openModal: (modal) => dispatch(openModal(modal)),
  closeModal: () => dispatch(closeModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(RightDockContainer);
